/**
 * A basic implementation of a non-intrusive doubly linked list.
 */
export class DoublyLinkedList {
    constructor() {
        this._head = null;
        this._tail = null;
        this.len = 0;
    }
    /**
     * Returns the current head of the linked list
     */
    get head() {
        return this._head;
    }
    /**
     * Returns the current tail of the linked list
     */
    get tail() {
        return this._tail;
    }
    /**
     * Returns the current number of elements in the linked list
     */
    get length() {
        return this.len;
    }
    /**
     * Constructs a new node that will contain the payload 'data' and inserts it onto the tail of the list.
     *
     * The 'data' payload is optional. When left unspecified the returned node is created without a 'data' field.
     * @param data The node payload
     */
    add(data) {
        // Construct a new node that will be pushed onto the tail of the list. By setting 'prev' to '_tail' we make a
        // node after the tail (or after nothing if there's no tail).
        const node = {
            next: null,
            prev: this._tail,
            data,
        };
        // Check if we have a _tail
        if (this._tail !== null) {
            // If we have a tail already then update 'next' (which must be null) to point at our new node, forming a
            // valid linked list. We then replace '_tail' with our new node as it has become the new list tail.
            this._tail.next = node;
            this._tail = node;
        }
        else {
            // If we have no tail then the list is empty, so just make the node the head *and* tail.
            this._head = node;
            this._tail = node;
        }
        this.len += 1;
        return node;
    }
    /**
     * Removes the given node from the list.
     *
     * Calling 'remove' with a node that is not in the target list is undefined behavior.
     *
     * @param node The node to remove. Must be a member of 'this' when called.
     */
    remove(node) {
        // Get writable access to the node's links
        const _node = node;
        if (_node.next !== null && _node.prev !== null) {
            // Remove a node that is somewhere in the middle of the list
            _node.prev.next = _node.next;
            _node.next.prev = _node.prev;
        }
        else if (_node.prev !== null) {
            // Remove the tail of the list
            _node.prev.next = null;
            this._tail = _node.prev;
        }
        else if (_node.next !== null) {
            // Remove the head of the list
            _node.next.prev = null;
            this._head = _node.next;
        }
        else {
            // Remove the last node in the list
            this._head = null;
            this._tail = null;
        }
        // Decrement the length (this is unconditional)
        this.len -= 1;
    }
    /**
     * Takes two nodes 'node' and 'nextNode' that are members of 'this' and removes 'nextNode' from it's current place
     * in the list and places it as the immediate next node for 'node'.
     *
     * Calling 'splice' with 'node' or 'nextNode' that are not in the target list is undefined behavior.
     * @param node The anchor node we're moving to
     * @param nextNode The node which will be moved to be next from 'node'
     */
    splice(node, nextNode) {
        // Cast input for writable link access
        const _node = node;
        const _nextNode = nextNode;
        // If the two nodes are the same then there's nothing to do
        if (_node === _nextNode)
            return;
        // First we remove nextNode from the list with our existing implementation ready to insert into another place
        // in the list.
        this.remove(_nextNode);
        this.len += 1; // We don't actually remove the node, so we undo the length decrement
        // Push 'nextNode' after 'node'
        const oldNodeNext = _node.next;
        _node.next = _nextNode;
        if (this._tail === _node) {
            // If 'node' was the tail of the list then 'nextNode' has become the new tail
            this._tail = _nextNode;
        }
        else {
            // If 'node' was anything other than the tail then we need to update the old 'next' to point at 'nextNode'
            if (oldNodeNext !== null) {
                oldNodeNext.prev = _nextNode;
            }
        }
        // Update 'nextNode' with its new links so it knows where it is in the list
        _nextNode.next = oldNodeNext;
        _nextNode.prev = _node;
    }
}
